<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import { CUSTOMER_SOURCES, type CustomerPayload, type CustomerSource } from '@/services/customers'

/**
 * Formulario de alta y edición de cliente.
 *
 * Compartido por Create y Edit por el mismo motivo que en el catálogo: el
 * formulario y sus reglas son los mismos, y duplicarlos haría que una
 * corrección se aplicara en un archivo y se olvidara en el otro.
 *
 * La validación espeja la del backend solo en lo que el usuario puede corregir
 * sin viaje de ida y vuelta. La unicidad de `document` depende del estado del
 * servidor, así que no se replica: la devuelve el 422 y se pinta en su campo.
 */

interface FormState {
  name: string
  document: string
  email: string
  phone: string
  company: string
  city: string
  address: string
  source: CustomerSource | ''
}

const props = withDefaults(
  defineProps<{
    values?: Partial<Record<keyof FormState, string | null>>
    submitLabel: string
    loading?: boolean
    serverErrors?: Record<string, string[]>
  }>(),
  { loading: false, serverErrors: () => ({}) },
)

const emit = defineEmits<{
  submit: [payload: CustomerPayload]
  cancel: []
}>()

const sourceOptions = [
  { label: 'Sin especificar', value: '' },
  ...CUSTOMER_SOURCES.map((source) => ({ label: source, value: source })),
]

function emptyForm(): FormState {
  return {
    name: '',
    document: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    address: '',
    source: '',
  }
}

function fromValues(values?: Partial<Record<keyof FormState, string | null>>): FormState {
  if (!values) return emptyForm()
  return {
    name: values.name ?? '',
    // `null` y `''` significan lo mismo para el formulario: campo vacío. La
    // diferencia entre "no enviar" y "enviar vacío" la resuelve el backend con
    // las reglas `sometimes` / `nullable`.
    document: values.document ?? '',
    email: values.email ?? '',
    phone: values.phone ?? '',
    company: values.company ?? '',
    city: values.city ?? '',
    address: values.address ?? '',
    source: (values.source as CustomerSource | null) ?? '',
  }
}

const form = reactive<FormState>(fromValues(props.values))

watch(
  () => props.values,
  (next) => Object.assign(form, fromValues(next)),
)

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const fields = ['name', 'document', 'email', 'phone', 'company', 'city', 'address', 'source'] as const

/**
 * Mensaje por campo, combinando el error de cliente y el del servidor. El del
 * servidor gana porque explica la causa real: "ya existe un cliente con ese
 * documento" no se puede deducir en el cliente.
 */
const messages = computed<Record<(typeof fields)[number], string | null>>(() => {
  const name = form.name.trim()

  const client: Record<(typeof fields)[number], string | null> = {
    name: name === '' ? 'El nombre es obligatorio' : name.length > 255 ? 'Máximo 255 caracteres' : null,
    // El documento es opcional de verdad: un cliente puede no tener NIT, y
    // obligarlo a inventar uno solo para pasar la validación sería peor.
    document: form.document.length > 255 ? 'Máximo 255 caracteres' : null,
    email:
      form.email.trim() !== '' && !EMAIL_PATTERN.test(form.email.trim())
        ? 'El email no tiene un formato válido'
        : form.email.length > 255
          ? 'Máximo 255 caracteres'
          : null,
    phone: form.phone.length > 50 ? 'Máximo 50 caracteres' : null,
    company: form.company.length > 255 ? 'Máximo 255 caracteres' : null,
    city: form.city.length > 120 ? 'Máximo 120 caracteres' : null,
    address: form.address.length > 255 ? 'Máximo 255 caracteres' : null,
    source: null,
  }

  const result = {} as Record<(typeof fields)[number], string | null>
  for (const field of fields) {
    const fromServer = props.serverErrors[field]
    result[field] = fromServer && fromServer.length > 0 ? fromServer[0] : client[field]
  }
  return result
})

const hasErrors = computed(() => fields.some((field) => messages.value[field] !== null))

/** Cadena vacía → `null`, para que el backend guarde "sin valor" y no "". */
function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function handleSubmit(): void {
  if (hasErrors.value) return

  emit('submit', {
    name: form.name.trim(),
    document: orNull(form.document),
    email: orNull(form.email),
    phone: orNull(form.phone),
    company: orNull(form.company),
    city: orNull(form.city),
    address: orNull(form.address),
    source: orNull(form.source),
  })
}
</script>

<template>
  <AppCard>
    <template #header>
      <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
        Datos del cliente
      </h2>
    </template>

    <form
      class="space-y-5"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div class="space-y-2">
        <label
          for="customer-name"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Nombre comercial <span class="text-red-500">*</span>
        </label>
        <InputText
          id="customer-name"
          v-model="form.name"
          placeholder="Comercial Andina S.A.S."
          class="w-full"
          :invalid="!!messages.name"
          maxlength="255"
        />
        <p
          v-if="messages.name"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ messages.name }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div class="space-y-2">
          <label
            for="customer-document"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Documento
          </label>
          <InputText
            id="customer-document"
            v-model="form.document"
            placeholder="900123456-1"
            class="w-full"
            :invalid="!!messages.document"
            maxlength="255"
          />
          <p
            v-if="messages.document"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ messages.document }}
          </p>
          <p
            v-else
            class="text-xs text-surface-500 dark:text-surface-400"
          >
            Opcional. Es la identificación fiscal y debe ser única.
          </p>
        </div>

        <div class="space-y-2">
          <label
            for="customer-phone"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Teléfono
          </label>
          <InputText
            id="customer-phone"
            v-model="form.phone"
            placeholder="3001234567"
            inputmode="tel"
            class="w-full"
            :invalid="!!messages.phone"
            maxlength="50"
          />
          <p
            v-if="messages.phone"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ messages.phone }}
          </p>
        </div>
      </div>

      <div class="space-y-2">
        <label
          for="customer-email"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Email
        </label>
        <InputText
          id="customer-email"
          v-model="form.email"
          placeholder="contacto@empresa.com"
          type="email"
          class="w-full"
          :invalid="!!messages.email"
          maxlength="255"
        />
        <p
          v-if="messages.email"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ messages.email }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div class="space-y-2">
          <label
            for="customer-company"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Empresa
          </label>
          <InputText
            id="customer-company"
            v-model="form.company"
            placeholder="Nombre de la empresa"
            class="w-full"
            :invalid="!!messages.company"
            maxlength="255"
          />
          <p
            v-if="messages.company"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ messages.company }}
          </p>
        </div>

        <div class="space-y-2">
          <label
            for="customer-city"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Ciudad
          </label>
          <InputText
            id="customer-city"
            v-model="form.city"
            placeholder="Bogotá"
            class="w-full"
            :invalid="!!messages.city"
            maxlength="120"
          />
          <p
            v-if="messages.city"
            class="text-sm text-red-600 dark:text-red-400"
          >
            {{ messages.city }}
          </p>
        </div>
      </div>

      <div class="space-y-2">
        <label
          for="customer-address"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Dirección
        </label>
        <InputText
          id="customer-address"
          v-model="form.address"
          placeholder="Calle 100 #15-20"
          class="w-full"
          :invalid="!!messages.address"
          maxlength="255"
        />
        <p
          v-if="messages.address"
          class="text-sm text-red-600 dark:text-red-400"
        >
          {{ messages.address }}
        </p>
      </div>

      <div class="space-y-2">
        <label
          for="customer-source"
          class="block text-sm font-medium text-surface-700 dark:text-surface-300"
        >
          Origen
        </label>
        <Select
          id="customer-source"
          v-model="form.source"
          :options="sourceOptions"
          option-label="label"
          option-value="value"
          placeholder="¿Cómo llegó el cliente?"
          class="w-full"
        />
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-surface-200 pt-5 sm:flex-row sm:justify-end dark:border-surface-700">
        <Button
          type="button"
          label="Cancelar"
          severity="secondary"
          :disabled="loading"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          :label="submitLabel"
          icon="pi pi-check"
          :loading="loading"
        />
      </div>
    </form>
  </AppCard>
</template>
